<script setup lang="ts">
import Simplebar from "simplebar-vue";

interface Metric {
  name: string;
  value: string;
  differenceWithBest: string | null;
  better: boolean | null;
}
interface Item {
  key: string;
  metrics: Metric[];
}

const { items } = defineProps<{ items: Item[] }>();

const explanations = defineModel<string[]>("explanations", { required: true });
</script>

<template>
  <div class="estimator-report-comparison-custom-modal-body">
    <Simplebar class="scroller">
      <div class="items">
        <div v-for="(item, index) in items" :key="item.key" class="card item">
          <div class="name text-base-medium">
            <div><i class="icon icon-cube" /> {{ item.key }}</div>
          </div>

          <div class="metrics text-base-medium">
            <div v-for="metric in item.metrics" :key="metric.name" class="metric">
              <span class="number">
                {{ metric.value }}
              </span>

              <span
                v-if="metric.differenceWithBest"
                class="difference"
                :class="{ better: metric.better }"
              >
                (
                <span class="number">{{ metric.differenceWithBest ?? "" }}</span>
                )
              </span>

              <span class="metric-name">({{ metric.name }})</span>
            </div>
          </div>

          <div class="editor card-content">
            <textarea v-model="explanations[index]" :focus="true" />
          </div>
        </div>
      </div>
    </Simplebar>
  </div>
</template>

<style scoped>
.estimator-report-comparison-custom-modal-body {
  .scroller {
    max-height: 70dvh;
  }

  :deep(.simplebar-track.simplebar-vertical) {
    transform: translateX(12px);
  }

  .items {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-16);
  }

  .item {
    display: flex;
    flex-direction: column;
    padding: var(--numbers-12);
    border-radius: var(--numbers-8);
    gap: var(--numbers-12);

    .name {
      > div {
        display: inline-block;
        padding: var(--numbers-4) var(--numbers-6);
        border: var(--stroke-width-md) solid var(--border-tertiary);
        border-radius: var(--numbers-8);
        background-color: var(--background-primary);
      }

      .icon {
        color: var(--content-brand-2);
        vertical-align: middle;
      }
    }

    .metrics {
      display: flex;
      flex-direction: row;
      gap: var(--numbers-12);

      .metric-name {
        padding-left: var(--numbers-4);
        color: var(--content-secondary);
      }
    }

    .difference {
      color: var(--content-negative);

      &.better {
        color: var(--content-positive);
      }
    }

    .editor {
      height: var(--numbers-128);
      padding: var(--numbers-4);
      border-radius: var(--numbers-8);

      textarea {
        width: 100%;
        height: 100%;
        border: none;
        background-color: transparent;
        color: var(--content-secondary);
        font-family: GeistMono, monospace;
        resize: none;

        &:focus {
          outline: none;
        }

        &:focus-visible {
          box-shadow: none;
          outline: none;
        }
      }
    }
  }
}
</style>
